<template>
  <div v-if="vrifiedAssets.length" class="striped">
    <section id="assets" class="container vrified-assets">
      <ModalsShare
        data-testid="share-deck-btn"
        :showing="isShareMenuOpen"
        :title="deckToBeShared.title"
        :share-url="deckToBeShared.shareUrl"
        analytics-prefix="CompanyPage"
        @update:showing="isShareMenuOpen = false"
      />
      <div v-for="asset in vrifiedAssets" :key="asset.id" class="asset" data-testid="vrified-asset">
        <h2 class="tracking-normal font-medium text-lg mb-2 uppercase">VRIFIED Asset</h2>
        <div class="asset-name text-lg font-semibold mb-1 md_mb-0 md_text-4xl" data-testid="vrified-asset-name">{{ asset.name }}</div>
        <div
          v-if="asset.details"
          class="asset-details text-gray-800 my-3 flex leading-6 md_my-0 md_text-lg"
          data-testid="vrified-asset-details"
        >
          {{ asset.details }}
        </div>
        <CompaniesDeckCarousel class="lg_hidden" :decks="asset.decks" />
        <div v-if="asset.decks.length > 0" class="hidden lg_flex flex-wrap gap-x-4 gap-y-4 mt-8">
          <div v-for="deck in asset.decks" :key="deck.id" class="deck-slides bg-white">
            <div class="deck-content cursor-pointer flex flex-col md_flex-row gap-4 w-full" @click="goToDeck(deck)">
              <div class="image w-full md_w-auto flex-shrink-0 lg_h-28 xl_h-32">
                <img v-if="deck.file" class="object-cover" :src="deck.file.url || undefined" />
                <div v-else class="bg-gray-300" />
              </div>
              <div class="info flex-grow">
                <button
                  class="cta float-right ml-2 appearance-none p-0 bg-transparent"
                  data-testid="carousel-deck-share-btn"
                  @click.stop="shareDeck(deck)"
                >
                  <ArrowUpTrayIcon class="w-6" />
                </button>
                <VTag
                  v-for="tag in deck.type_tags"
                  :key="tag"
                  class="type inline-block mr-3 mb-2"
                  data-testid="carousel-deck-type"
                  :text="tag"
                />
                <div class="title text-gray-900 text-xs mb-2 md_text-sm pb-8 md_pb-0" data-testid="carousel-deck-title">
                  {{ deck.title }}
                </div>
                <div class="text-gray-600 mt-auto text-xs mb-2" data-testid="carousel-deck-last-updated">
                  {{ formatDateFull(deck.updated_at) }}
                </div>
                <div class="tags flex flex-wrap" data-testid="carousel-deck-tags">
                  <VAssetTag v-for="(tag, index) in deck.tags" :key="index" class="mb-2 mr-3" :tag="tag" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { ArrowUpTrayIcon } from '@heroicons/vue/24/outline'
import type { PropType, DeepReadonly, Ref } from 'vue'
import { getCommodityByproducts, getPercentOwnership, getPrimaryCommodity } from '@/lib/companyPageHelpers'
import { formatDateFull } from '@/lib/datetime-format-utils'
import type { Asset } from '@/entities/asset'
import type { Deck } from '@/entities/deck'

const props = defineProps({
  assets: {
    type: Array as PropType<DeepReadonly<Asset[]>>,
    default: () => [],
  },
})

const { goToDeck } = useEndpointNavigation()
const isShareMenuOpen: Ref<boolean> = ref(false)

const vrifiedAssets = computed(() =>
  props.assets
    .filter(asset => asset.is_vrified)
    .map(asset => ({
      ...asset,
      details: assetDetails(asset),
    }))
)

function assetDetails(asset: DeepReadonly<Asset>) {
  return [
    asset.region?.name,
    [getPrimaryCommodity(asset.commodities), ...getCommodityByproducts(asset.commodities)].join(', '),
    asset.stage?.name,
    asset.ownership && `${getPercentOwnership(asset.ownership)} Ownership`,
  ]
    .filter(x => x)
    .join(' \u2022 ')
}

const deckToBeShared = reactive({
  title: '',
  shareUrl: '',
})

const getDeckUrl = (deckUrlSlug: string | number) => {
  return `${window.location.origin}/decks/${deckUrlSlug}`
}

const shareDeck = (deck: DeepReadonly<Deck>) => {
  deckToBeShared.title = deck.title ?? ''
  deckToBeShared.shareUrl = getDeckUrl(deck.url_slug || deck.id)

  isShareMenuOpen.value = true
}
</script>

<style lang="stylus" scoped>
.asset:not(:last-child)
  @apply mb-28

.asset-tile
  grid-template-columns: 22.25rem 1fr
  grid-template-rows: repeat(4, min-content) 1fr min-content

.deck-slides
  @apply w-full border border-gray-200 p-4 text-sm rounded-md flex-grow
  width calc(50% - 0.5rem)
  max-width 50%

.deck-content .image
  aspect-ratio 16 / 9
  & > *
    @apply w-full h-full rounded-md

@supports not (aspect-ratio: 1 / 1)
  .deck-content .image.h-24
    width 6rem * (16 / 9)

  @screen sm
    .deck-content .image.sm_h-28
      width 7rem * (16 / 9)

  @screen md
    .deck-content .image.md_h-32
      width 8rem * (16 / 9)

  @screen lg
    .deck-content .image.lg_h-36
      width 9rem * (16 / 9)
</style>
